import { FC, useState } from "react"
import { useQuery } from "react-query"

import { InputText } from "@synapse-analytics/synapse-ui"

import { VisionAPI } from "../API/VisionAPI"
import { definitions } from "../types/Generated/apiTypes"
import auth from "../utils/auth"

type Branch = definitions["Branch"]

interface BranchesAutocompleteProps {
  /** Selected branch IDs */
  selectedValues: number[]
  /** Callback when selected values change */
  onSelectionChange: (branches: number[]) => void
  /** Optional: Pre-provided branches list. If not provided, branches will be fetched automatically */
  branches?: Branch[]
  /** Optional: Custom label for the input */
  label?: string
  /** Optional: Custom placeholder */
  placeholder?: string
  /** Whether the field is required */
  required?: boolean
  /** Whether to allow deselecting branches */
  allowDeselect?: boolean
  /** Additional props to pass to InputText */
  fullWidth?: boolean
  variant?: "filled" | "outlined"
  hideDescription?: boolean
  error?: string
  handleBlur?: () => void
}

const BranchesAutocomplete: FC<BranchesAutocompleteProps> = ({
  selectedValues,
  onSelectionChange,
  branches: providedBranches,
  label = "Branches",
  placeholder = "search for a branch",
  required = false,
  allowDeselect = true,
  fullWidth = true,
  variant = "filled",
  hideDescription = true,
  error,
  handleBlur,
}) => {
  const [searchValue, setSearchValue] = useState("")

  const userId = auth.getUserId()

  // Fetch branches if not provided
  const { data: fetchedBranches, isLoading: isBranchesListLoading } = useQuery<Branch[]>(
    ["fetchUserBranches", userId],
    ({ queryKey }) => VisionAPI.fetchUserBranches({ user_id: queryKey[1] as string }),
    {
      enabled: !!userId && !providedBranches,
    }
  )

  // Use provided branches or fetched branches
  const branches = providedBranches || fetchedBranches || []
  const isLoading = !providedBranches && isBranchesListLoading

  // Map branches to options format
  const mappedBranches =
    branches?.map((branch) => ({
      label: branch.name,
      value: branch.id!,
    })) || []

  return (
    <InputText
      fullWidth={fullWidth}
      required={required}
      value={searchValue}
      handleChange={(event) => setSearchValue(event.target.value as string)}
      variant={variant}
      handleBlur={handleBlur}
      multipleModeProps={{
        isActive: true,
        selectedValues: selectedValues,
        setSelectedValues: (values) => onSelectionChange(values.map((v) => Number(v))),
        allowDeselect: allowDeselect,
      }}
      placeholder={placeholder}
      loading={isLoading}
      optionsWithValues={mappedBranches}
      hideDescription={hideDescription}
      label={label}
      error={error}
    />
  )
}

export default BranchesAutocomplete
